import {
  CaseNoteModel,
  type ICaseNote,
  type ICaseNotesResponse,
  type PostCaseNote
} from '@/models/caseNote';
import { DateTime } from 'luxon';
import { signedApiClient } from './apiClient';

type CaseNotesPayload = {
  notes: CaseNoteModel[];
  nmpStartTime?: string;
};

const parseNmpStartTime = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = DateTime.fromISO(value, { setZone: true });
  return parsed.isValid ? value : undefined;
};

class CaseNoteService {
  private readonly caseEndpoint: string = 'case';

  async getNotes(caseId: string): Promise<CaseNotesPayload> {
    const uri = `${this.caseEndpoint}/${caseId}/notes`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    const responseData = response.data as ICaseNotesResponse;

    return {
      notes: (responseData.notes as ICaseNote[]).map(CaseNoteModel.mapFromBackend),
      nmpStartTime: parseNmpStartTime(responseData.nmpStartTime)
    };
  }

  async createNote(caseId: string, note: PostCaseNote): Promise<CaseNoteModel> {
    const uri = `${this.caseEndpoint}/${caseId}/notes`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, note);
    return CaseNoteModel.mapFromBackend(response.data as ICaseNote);
  }

  async updateNote(caseId: string, noteId: string, note: PostCaseNote): Promise<CaseNoteModel> {
    const uri = `${this.caseEndpoint}/${caseId}/notes/${noteId}`;
    const apiClient = signedApiClient('PATCH', uri);
    const response = await apiClient.patch(uri, note);
    return CaseNoteModel.mapFromBackend(response.data as ICaseNote);
  }

  async deleteNote(caseId: string, noteId: string): Promise<void> {
    const uri = `${this.caseEndpoint}/${caseId}/notes/${noteId}`;
    const apiClient = signedApiClient('DELETE', uri);
    await apiClient.delete(uri);
  }
}

export default new CaseNoteService();
